import { FAQ_CATEGORIES } from '../data/faqContent';

export type MatchedFaq = {
  categoryTitle: string;
  question: string;
  answerText: string;
};

/**
 * Lightweight keyword score over FAQ corpus — no network, safe for instant replies.
 */
export function matchFaqs(query: string, limit = 4): MatchedFaq[] {
  const raw = query.trim().toLowerCase();
  if (!raw) return [];

  const tokens = raw
    .split(/\s+/)
    .map((w) => w.replace(/[^\w'-]/g, ''))
    .filter((w) => w.length > 1);

  const scored: { score: number; item: MatchedFaq }[] = [];

  for (const cat of FAQ_CATEGORIES) {
    for (const item of cat.items) {
      const blob = [
        cat.title,
        item.question,
        ...item.answer,
        item.matchText ?? '',
      ]
        .join(' ')
        .toLowerCase();

      let score = 0;
      if (blob.includes(raw)) score += 12;

      const qWords = item.question.toLowerCase().split(/\s+/);
      for (const t of tokens) {
        if (t.length < 3 && tokens.length > 2) continue;
        if (blob.includes(t)) score += 3;
        if (qWords.some((qw) => qw.startsWith(t) || t.startsWith(qw))) score += 2;
      }

      if (score > 0) {
        scored.push({
          score,
          item: {
            categoryTitle: cat.title,
            question: item.question,
            answerText: item.answer.join('\n\n'),
          },
        });
      }
    }
  }

  scored.sort((a, b) => b.score - a.score);
  const dedup = new Map<string, MatchedFaq>();
  for (const row of scored) {
    const k = row.item.question;
    if (!dedup.has(k)) dedup.set(k, row.item);
    if (dedup.size >= limit) break;
  }
  return [...dedup.values()];
}

/** For chat grounding: pass `maxAnswerChars` so answers stay short snippets (easier for the LLM to paraphrase). */
export function matchesToContext(
  matches: MatchedFaq[],
  opts?: { maxAnswerChars?: number },
): string {
  if (!matches.length) return '';
  const cap = opts?.maxAnswerChars;
  return matches
    .map((m, i) => {
      let a = m.answerText;
      if (typeof cap === 'number' && cap > 0 && a.length > cap) {
        a = `${a.slice(0, cap).trim()}…`;
      }
      return `[${i + 1}] (${m.categoryTitle}) Q: ${m.question}\nA: ${a}`;
    })
    .join('\n\n---\n\n');
}
